import { Array, Effect, pipe, Ref } from "effect";
import { getDisplayMarkets } from "./get-display-markets";
import {
  Address,
  makeAddressSync,
  prodProtocolKeys,
  ProtocolKey,
} from "@silo/types";
import {
  IncentivizedShareToken,
  IncentivizedShareTokensService,
} from "./types";
import { getProtocolConfig } from "@silo/config";
import { isNullable } from "effect/Predicate";

export type ValidateIncentivizedShareTokens = ReturnType<
  typeof validateIncentivizedShareTokens
>;
export type ValidateIncentivizedShareTokensSuccess =
  Effect.Effect.Success<ValidateIncentivizedShareTokens>;
export type ValidateIncentivizedShareTokensError =
  Effect.Effect.Error<ValidateIncentivizedShareTokens>;
export type ValidateIncentivizedShareTokensContext =
  Effect.Effect.Context<ValidateIncentivizedShareTokens>;

/**
 * Using the markets page data, determines if any share tokens are actively
 * receiving rewards that are NOT in the configured historical share token
 * array.
 */
export function validateIncentivizedShareTokens() {
  return Effect.gen(function* () {
    yield* getDisplayMarkets([...prodProtocolKeys]);

    const ref = yield* IncentivizedShareTokensService;

    const incentivizedShareTokens = yield* Ref.get(ref);

    const result = prodProtocolKeys.map((protocolKey) => ({
      protocolKey,
      missingShareTokens: getMissingShareTokens(
        protocolKey,
        incentivizedShareTokens
      ),
    }));

    return result;
  }).pipe(
    Effect.provideServiceEffect(
      IncentivizedShareTokensService,
      Ref.make<IncentivizedShareToken[]>([])
    )
  );
}

function getMissingShareTokens(
  protocolKey: ProtocolKey,
  incentivizedShareTokens: IncentivizedShareToken[]
): Address[] {
  const { nativeRewards } = getProtocolConfig(protocolKey);

  if (isNullable(nativeRewards)) return [];

  const protocolShareTokens = incentivizedShareTokens.filter(
    (token) => token.protocolKey === protocolKey
  );

  const historicalShareTokens = pipe(
    nativeRewards.historicalShareTokens[nativeRewards.tokenAddress] ?? [],
    Array.map(makeAddressSync),
    (arr) => new Set(arr)
  );

  const activeShareTokens = pipe(
    protocolShareTokens.map((token) => token.shareTokenAddress),
    Array.map(makeAddressSync),
    (arr) => new Set(arr)
  );

  const diff = [...activeShareTokens].filter(
    (x) => !historicalShareTokens.has(x)
  );

  return diff;
}
